import { useCallback, useEffect } from 'react';
import { SubmitHandler, UseFormReturn } from 'react-hook-form';

import { useDebounceCallback } from 'usehooks-ts';

import urls from '../../../../../../db/polls/urls';
import { AttachmentRequestConfig } from '../../../../../../db/types';
import { usePollContext } from '../../../../Context';
import { POLL_SAVE_DEBOUNCE_TIME } from '../../../constants';
import { FormValues } from '../../../PollQuestionForm/types';

export interface UseUnpublishedQuestionDescription {
  formMethods: UseFormReturn<FormValues>;
  onSubmit: SubmitHandler<FormValues>;
  onLocalSubmit: SubmitHandler<FormValues>;
}

const useUnpublishedQuestionDescription = ({
  formMethods,
  onSubmit,
  onLocalSubmit,
}: UseUnpublishedQuestionDescription) => {
  const { poll } = usePollContext();

  const uploadPollAttachmentConfig:
    | AttachmentRequestConfig<{ activityType: 'polls'; activityId: MongoId }>
    | undefined = poll
    ? {
        url: urls.uploadPollFile,
        data: { activityType: 'polls', activityId: poll.id },
      }
    : undefined;

  const { handleSubmit } = formMethods;

  const onChange = useCallback(
    async (value: string) => {
      const getUpdatedData = (data: FormValues) => {
        return {
          ...data,
          description: value,
        };
      };

      const submitForm = handleSubmit((data) => onSubmit(getUpdatedData(data)));
      await submitForm();

      const localSubmitForm = handleSubmit((data) => onLocalSubmit(getUpdatedData(data)));
      await localSubmitForm();
    },
    [handleSubmit, onSubmit, onLocalSubmit]
  );

  const handleChange = useDebounceCallback(onChange, POLL_SAVE_DEBOUNCE_TIME);

  const handleBlur = useCallback(() => {
    handleSubmit(onLocalSubmit)();
  }, [handleSubmit, onLocalSubmit]);

  const handleAttachmentsChange = useCallback(() => {
    handleSubmit(onSubmit)();
    handleSubmit(onLocalSubmit)();
  }, [handleSubmit, onSubmit]);

  return { uploadPollAttachmentConfig, handleChange, handleBlur, handleAttachmentsChange };
};

export default useUnpublishedQuestionDescription;
